// Compare v3 — multi-column player comparison with per-column season selectors.
//
// Route: /compare?p=slug1:2025~Euroleague&p=slug2&p=slug3:2024~Liga%20ACB...
// Backward compat: /compare?a=slug1&b=slug2 (maps to ?p=slug1&p=slug2)
//
// Up to 5 player columns. Stats as ROWS (GP, PTS, REB, AST, eFG%, TS%), players
// as COLUMNS. Each column has its own season+league selector; coaches can align
// all to one season or compare each player's best independently.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { apiJson } from '../lib/api'
import Avatar from '../components/Avatar'
import type { AvailabilityStatus, PlayerPosition } from '../types/index'
import { useCompare } from '../context/CompareContext'

interface SeasonsResponse {
  player: {
    name: string
    slug: string
    position: PlayerPosition | null
    photo_url: string | null
    height_cm: number | null
    current_team: {
      team_name: string
      league_name: string
    } | null
    availability_status: AvailabilityStatus
  }
  seasons: Array<{
    season: string
    league: string
    team: string | null
    games_played: number | null
    points_pg: number | null
    rebounds_pg: number | null
    assists_pg: number | null
    efg_pct: number | null
    ts_pct: number | null
    // Deeper splits (2026-07-25) — mpg + computed 0–1 pct fractions
    // (NULL when the row's shooting totals are missing/invalid).
    mpg: number | null
    fg_pct: number | null
    three_pct: number | null
    ft_pct: number | null
    fga_total: number | null
    fta_total: number | null
    // Volume tooltips (2026-07-26) — remaining makes/attempts totals for
    // the pct-cell "12-31" sub-text + low-volume marker.
    fgm_total: number | null
    tpm_total: number | null
    tpa_total: number | null
    ftm_total: number | null
    is_3x3: boolean
  }>
}

interface SearchResult {
  id: string
  slug: string
  name: string
  photo_url: string | null
  position: PlayerPosition | null
  nationality: string | null
  is_verified: boolean
}

interface SearchResponse {
  results: SearchResult[]
  has_more: boolean
}

interface PlayerColumn {
  slug: string
  initialSeason: string | null // "SEASON~LEAGUE" or null for default
  data: SeasonsResponse | null
  loading: boolean
  error: string | null
  selectedIndex: number | null // index into data.seasons (after 3x3 filter)
}

// Parse URL token "slug" or "slug:SEASON~LEAGUE".
function parseToken(token: string): { slug: string; season: string | null } {
  const parts = token.split(':')
  if (parts.length === 1) return { slug: parts[0]!, season: null }
  return { slug: parts[0]!, season: parts[1] ?? null }
}

// Serialize column to URL token.
function serializeColumn(col: PlayerColumn): string {
  if (!col.data || col.selectedIndex === null) return col.slug
  const seasons = col.data.seasons.filter((s) => !s.is_3x3)
  const selected = seasons[col.selectedIndex]
  if (!selected) return col.slug
  return `${col.slug}:${selected.season}~${encodeURIComponent(selected.league)}`
}

// Format stat as displayable string. null → "—".
function formatStat(val: number | null): string {
  if (val == null) return '—'
  return val.toFixed(1)
}

// Format efficiency percentage (stored as fraction 0–1, display as %).
function formatEffPct(val: number | null): string {
  if (val == null) return '—'
  return `${(val * 100).toFixed(1)}%`
}

const MAX_COLUMNS = 5

// Volume tooltips (2026-07-26) — the accepted limitation is that stat
// percentages have NO attempt-volume guard in the query (1-for-1 = 100%).
// Guard the UX instead: pct cells show makes-attempts sub-text, and splits
// under this attempt count get a low-volume marker. ONE constant, one place.
const LOW_VOLUME_ATTEMPTS = 20

type SeasonRow = SeasonsResponse['seasons'][number]

// makes/attempts for a pct row key, or null when totals are missing.
// FG is the all-FG basis (fg2 + 3PT) — same basis as the computed fg_pct.
function volumeFor(
  season: SeasonRow,
  key: string,
): { makes: number; attempts: number } | null {
  if (key === 'fg_pct') {
    if (
      season.fgm_total == null ||
      season.fga_total == null ||
      season.tpm_total == null ||
      season.tpa_total == null
    ) {
      return null
    }
    return {
      makes: season.fgm_total + season.tpm_total,
      attempts: season.fga_total + season.tpa_total,
    }
  }
  if (key === 'three_pct') {
    if (season.tpm_total == null || season.tpa_total == null) return null
    return { makes: season.tpm_total, attempts: season.tpa_total }
  }
  if (key === 'ft_pct') {
    if (season.ftm_total == null || season.fta_total == null) return null
    return { makes: season.ftm_total, attempts: season.fta_total }
  }
  return null
}

const STATS_ROWS = [
  { key: 'games_played', label: 'GP', format: (v: number | null) => v != null ? String(v) : '—' },
  // Deeper splits (2026-07-25) — MPG + the three computed shooting
  // percentages. Same N-way best-value highlight semantics as the rest
  // (max wins, needs ≥2 non-NULL — computeBestColumns is key-agnostic).
  { key: 'mpg', label: 'MPG', format: formatStat },
  { key: 'points_pg', label: 'PTS', format: formatStat },
  { key: 'rebounds_pg', label: 'REB', format: formatStat },
  { key: 'assists_pg', label: 'AST', format: formatStat },
  { key: 'fg_pct', label: 'FG%', format: formatEffPct },
  { key: 'three_pct', label: '3PT%', format: formatEffPct },
  { key: 'ft_pct', label: 'FT%', format: formatEffPct },
  { key: 'efg_pct', label: 'eFG%', format: formatEffPct },
  { key: 'ts_pct', label: 'TS%', format: formatEffPct },
] as const

export default function Compare() {
  const [searchParams, setSearchParams] = useSearchParams()
  const compare = useCompare()

  // Ref to suppress re-init when we write URL ourselves.
  const suppressNextInit = useRef(false)

  // Parse URL params. Backward compat: ?a=x&b=y → p[]=x, p[]=y.
  // Use searchParams.toString() as stable dep to avoid new-array-every-render bug.
  const urlSearchString = searchParams.toString()

  // Live ref of compare set — read inside the memo WITHOUT being a dep, so
  // on-page compare.add/remove (which change compare.slugs) can't re-trigger
  // column init and wipe loaded data / chosen seasons. URL is the source of
  // truth after mount; compare.hydrated is a dep so the one-time localStorage
  // hydration flip re-evaluates the no-params fallback exactly once.
  const compareSlugsRef = useRef(compare.slugs)
  compareSlugsRef.current = compare.slugs

  // null = not ready yet (waiting for localStorage hydration on a no-params URL).
  const initialTokens = useMemo<string[] | null>(() => {
    const sp = new URLSearchParams(urlSearchString)
    const pParams = sp.getAll('p')
    const aParam = sp.get('a')
    const bParam = sp.get('b')
    if (pParams.length > 0) return pParams.slice(0, MAX_COLUMNS)
    if (aParam && bParam) return [aParam, bParam]
    if (aParam) return [aParam]
    // No URL params → hydrate from compare set (once storage has loaded)
    if (!compare.hydrated) return null
    return compareSlugsRef.current.slice(0, MAX_COLUMNS)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearchString, compare.hydrated])

  const [columns, setColumns] = useState<PlayerColumn[]>([])

  // Picker state.
  const [showPicker, setShowPicker] = useState(false)
  const [pickerQuery, setPickerQuery] = useState('')
  const [pickerResults, setPickerResults] = useState<SearchResult[]>([])
  const [pickerLoading, setPickerLoading] = useState(false)

  // Height cap state (default 390cm, VBA combined import limit).
  // Parse from URL ?cap=N, fallback to 390 on absent/invalid.
  const capFromUrl = useMemo(() => {
    const capParam = searchParams.get('cap')
    const parsed = capParam ? parseInt(capParam, 10) : NaN
    return isNaN(parsed) ? 390 : parsed
  }, [searchParams])
  const [heightCap, setHeightCap] = useState(capFromUrl)

  // Sync heightCap to URL whenever it changes.
  useEffect(() => {
    if (heightCap === capFromUrl) return
    const sp = new URLSearchParams(searchParams)
    sp.set('cap', String(heightCap))
    suppressNextInit.current = true
    setSearchParams(sp, { replace: true })
  }, [heightCap, capFromUrl, searchParams, setSearchParams])

  // Initialize columns from URL on mount / URL change.
  useEffect(() => {
    // Not ready yet (no-params URL waiting on localStorage hydration).
    if (initialTokens === null) return
    // Skip if we just wrote the URL ourselves (via the sync effect).
    if (suppressNextInit.current) {
      suppressNextInit.current = false
      return
    }

    const parsed = initialTokens.map((token) => {
      const { slug, season } = parseToken(token)
      return {
        slug,
        initialSeason: season,
        data: null,
        loading: false,
        error: null,
        selectedIndex: null,
      } as PlayerColumn
    })
    setColumns(parsed)
  }, [initialTokens])

  // Sync compare set when URL params are present (shareable links).
  // Deps: URL string + hydration flag ONLY — depending on `compare` (a new
  // object every provider render) while calling compare.set() created an
  // infinite update loop. set() also bails on identical content as a second
  // guard. Wait for hydration so localStorage can't overwrite URL slugs.
  useEffect(() => {
    if (!compare.hydrated) return
    const sp = new URLSearchParams(urlSearchString)
    const pParams = sp.getAll('p')
    const aParam = sp.get('a')
    const bParam = sp.get('b')
    const urlSlugs: string[] = []
    if (pParams.length > 0) {
      urlSlugs.push(...pParams.map(t => parseToken(t).slug))
    } else if (aParam && bParam) {
      urlSlugs.push(aParam, bParam)
    } else if (aParam) {
      urlSlugs.push(aParam)
    }
    if (urlSlugs.length > 0) {
      compare.set(urlSlugs)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearchString, compare.hydrated])

  // Fetch data for each column.
  useEffect(() => {
    columns.forEach((col, idx) => {
      if (col.data || col.loading) return
      setColumns((prev) => {
        const next = [...prev]
        next[idx] = { ...next[idx]!, loading: true, error: null }
        return next
      })
      apiJson<SeasonsResponse>(`/api/players/${col.slug}/seasons`)
        .then((data) => {
          setColumns((prev) => {
            const next = [...prev]
            if (next[idx]?.slug !== col.slug) return prev // Stale
            const seasons = data.seasons.filter((s) => !s.is_3x3)
            let selectedIndex = 0
            if (col.initialSeason) {
              const [wantSeason, wantLeague] = col.initialSeason.split('~')
              const match = seasons.findIndex(
                (s) => s.season === wantSeason && s.league === decodeURIComponent(wantLeague ?? '')
              )
              if (match !== -1) selectedIndex = match
            } else {
              // Default: most recent season; tie-break highest games_played.
              if (seasons.length > 0) {
                const grouped = new Map<string, typeof seasons>()
                seasons.forEach((s) => {
                  if (!grouped.has(s.season)) grouped.set(s.season, [])
                  grouped.get(s.season)!.push(s)
                })
                const mostRecent = Array.from(grouped.keys()).sort((a, b) => b.localeCompare(a))[0]
                const candidates = grouped.get(mostRecent!) ?? []
                const best = candidates.sort((a, b) => {
                  const gpA = a.games_played ?? -1
                  const gpB = b.games_played ?? -1
                  return gpB - gpA
                })[0]
                selectedIndex = seasons.indexOf(best!)
              }
            }
            next[idx] = { ...next[idx]!, data, loading: false, selectedIndex: seasons.length > 0 ? selectedIndex : null }
            return next
          })
        })
        .catch((err) => {
          setColumns((prev) => {
            const next = [...prev]
            if (next[idx]?.slug !== col.slug) return prev
            next[idx] = { ...next[idx]!, loading: false, error: err.message || 'Failed to load' }
            return next
          })
        })
    })
  }, [columns])

  // Sync URL when columns or selections change (debounced).
  useEffect(() => {
    const timer = setTimeout(() => {
      if (columns.length === 0) return
      const tokens = columns.map(serializeColumn)
      const current = searchParams.getAll('p')
      const changed = tokens.length !== current.length || tokens.some((t, i) => t !== current[i])
      if (changed) {
        const params = new URLSearchParams()
        tokens.forEach((t) => params.append('p', t))
        // Set the flag to prevent the init effect from re-running.
        suppressNextInit.current = true
        setSearchParams(params, { replace: true })
      }
    }, 100)
    return () => clearTimeout(timer)
  }, [columns, searchParams, setSearchParams])

  // Player picker debounced search.
  useEffect(() => {
    if (!pickerQuery.trim()) {
      setPickerResults([])
      return
    }
    setPickerLoading(true)
    const timer = setTimeout(() => {
      apiJson<SearchResponse>(`/api/players/search?q=${encodeURIComponent(pickerQuery)}&limit=10`)
        .then((data) => {
          setPickerResults(data.results)
          setPickerLoading(false)
        })
        .catch(() => setPickerLoading(false))
    }, 300)
    return () => clearTimeout(timer)
  }, [pickerQuery])

  const handleAddPlayer = () => {
    setShowPicker(true)
  }

  const handlePickerSelect = (slug: string) => {
    if (columns.length >= MAX_COLUMNS) return
    compare.add(slug)
    setColumns((prev) => [
      ...prev,
      { slug, initialSeason: null, data: null, loading: false, error: null, selectedIndex: null },
    ])
    setShowPicker(false)
    setPickerQuery('')
  }

  const handleRemoveColumn = (idx: number) => {
    const slug = columns[idx]?.slug
    if (slug) compare.remove(slug)
    setColumns((prev) => prev.filter((_, i) => i !== idx))
  }

  const handleClearAll = () => {
    compare.clear()
    setColumns([])
  }

  const handleSeasonChange = (colIdx: number, seasonIdx: number) => {
    setColumns((prev) => {
      const next = [...prev]
      next[colIdx] = { ...next[colIdx]!, selectedIndex: seasonIdx }
      return next
    })
  }

  // Compute better-value highlights per stat row. Returns array of column indexes that are max.
  const computeBestColumns = (statKey: string): Set<number> => {
    const values = columns
      .map((col, idx) => {
        if (!col.data || col.selectedIndex === null) return { idx, val: null }
        const seasons = col.data.seasons.filter((s) => !s.is_3x3)
        const selected = seasons[col.selectedIndex]
        if (!selected) return { idx, val: null }
        return { idx, val: (selected as any)[statKey] as number | null }
      })
      .filter((x) => x.val != null)
    if (values.length < 2) return new Set()
    const max = Math.max(...values.map((x) => x.val!))
    return new Set(values.filter((x) => x.val === max).map((x) => x.idx))
  }

  // Compute combined height of selected players. Returns null if ANY player
  // lacks height_cm (partial coverage yields NULL, never 0 — the project rule
  // from functions/lib/stats.ts). Also returns count of missing.
  const combinedHeight = useMemo(() => {
    const heights = columns
      .filter(col => col.data != null)
      .map(col => col.data!.player.height_cm)
    const missing = heights.filter(h => h == null).length
    if (missing > 0) return { total: null, missing }
    const validHeights = heights.filter((h): h is number => h != null)
    const sum = validHeights.reduce((acc, h) => acc + h, 0)
    return { total: sum, missing: 0 }
  }, [columns])

  if (columns.length === 0) {
    return (
      <div className="min-h-screen bg-hoop-bg text-hoop-text">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <h1 className="text-3xl font-display font-bold mb-4">Player Compare</h1>
          <p className="text-hoop-text-2 mb-6">
            Compare up to 5 players side-by-side with per-column season selectors.
          </p>
          <button
            onClick={handleAddPlayer}
            className="px-4 py-2 bg-hoop-accent text-white rounded hover:opacity-90"
          >
            + Add player
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-hoop-bg text-hoop-text">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl font-display font-bold">Player Compare</h1>
          <div className="flex items-center gap-4">
            {columns.length > 0 && (
              <button
                onClick={handleClearAll}
                className="text-sm text-red-500 hover:underline"
              >
                Clear all
              </button>
            )}
            <Link to="/discover" className="text-sm text-hoop-accent hover:underline">
              Back to Discover
            </Link>
          </div>
        </div>

        {/* Height-cap budget readout */}
        <div className="mb-6 p-4 bg-hoop-surface border border-hoop-border rounded">
          <div className="flex items-center gap-4 flex-wrap">
            <label className="text-sm font-semibold">
              Height cap (cm):
              <input
                type="number"
                value={heightCap}
                onChange={(e) => setHeightCap(parseInt(e.target.value, 10) || 390)}
                className="ml-2 w-20 px-2 py-1 border border-hoop-border rounded text-hoop-text bg-white dark:bg-hoop-bg"
              />
            </label>
            <div className="text-sm">
              {combinedHeight.total == null ? (
                <span className="text-hoop-text-2">
                  Combined: <span className="font-mono">—</span>
                  {combinedHeight.missing > 0 && (
                    <span className="ml-2 text-xs">
                      ({combinedHeight.missing} player{combinedHeight.missing > 1 ? 's' : ''} missing height)
                    </span>
                  )}
                </span>
              ) : (
                <>
                  <span className="font-semibold">Combined: </span>
                  <span className={`font-mono ${combinedHeight.total > heightCap ? 'text-red-600 font-bold' : 'text-green-600'}`}>
                    {combinedHeight.total}cm
                  </span>
                  <span className="mx-2">/</span>
                  <span className="font-mono">{heightCap}cm</span>
                  {combinedHeight.total > heightCap ? (
                    <span className="ml-2 text-red-600 font-semibold">
                      (over by {combinedHeight.total - heightCap}cm)
                    </span>
                  ) : (
                    <span className="ml-2 text-green-600">
                      ({heightCap - combinedHeight.total}cm remaining)
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Horizontal scroll container */}
        <div className="overflow-x-auto -mx-4 px-4">
          <div className="inline-block min-w-full align-middle">
            {/* Content-width table: no w-full, so 1-2 player columns don't
                stretch across the page and blow up the season dropdowns. */}
            <table className="border-collapse text-sm font-stat">
              <thead>
                <tr>
                  {/* Empty corner cell for stat labels */}
                  <th className="sticky left-0 bg-hoop-bg z-10 text-left py-3 px-3 font-semibold border-b border-hoop-border min-w-[80px]">
                    Stat
                  </th>
                  {columns.map((col, idx) => (
                    <th
                      key={`${col.slug}-${idx}`}
                      className="text-center py-3 px-3 border-b border-hoop-border w-[230px] min-w-[160px] max-w-[230px]"
                    >
                      {col.loading && <div className="text-hoop-text-2">Loading...</div>}
                      {col.error && (
                        <div className="text-red-500 text-xs">
                          <p className="font-semibold">Not found</p>
                          <p>{col.error}</p>
                        </div>
                      )}
                      {col.data && (
                        <div className="space-y-2">
                          <div className="flex flex-col items-center">
                            <Avatar
                              src={col.data.player.photo_url}
                              name={col.data.player.name}
                              className="w-16 h-16 rounded-full mb-2"
                              textClassName="text-lg"
                            />
                            <Link
                              to={`/players/${col.data.player.slug}`}
                              className="font-semibold text-hoop-accent hover:underline text-center text-sm"
                            >
                              {col.data.player.name}
                            </Link>
                            {col.data.player.current_team && (
                              <p className="text-xs text-hoop-text-2">
                                {col.data.player.current_team.team_name}
                              </p>
                            )}
                            <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded bg-hoop-accent-light text-hoop-text">
                              {col.data.player.availability_status}
                            </span>
                          </div>
                          {/* Season selector */}
                          {(() => {
                            const seasons = col.data.seasons.filter((s) => !s.is_3x3)
                            if (seasons.length === 0) {
                              return (
                                <p className="text-xs text-hoop-text-2">
                                  No games logged yet
                                </p>
                              )
                            }
                            return (
                              <select
                                value={col.selectedIndex ?? 0}
                                onChange={(e) => handleSeasonChange(idx, Number(e.target.value))}
                                className="w-full px-2 py-1 text-xs border border-hoop-border rounded bg-white dark:bg-hoop-surface text-hoop-text-2"
                              >
                                {seasons.map((s, sIdx) => (
                                  <option key={`${s.season}-${s.league}`} value={sIdx}>
                                    {s.season} · {s.league}
                                  </option>
                                ))}
                              </select>
                            )
                          })()}
                          {/* Remove button */}
                          <button
                            onClick={() => handleRemoveColumn(idx)}
                            className="text-xs text-hoop-text-2 hover:text-red-500"
                          >
                            ✕ Remove
                          </button>
                        </div>
                      )}
                    </th>
                  ))}
                  {/* Add player affordance */}
                  {columns.length < MAX_COLUMNS && (
                    <th className="text-center py-3 px-3 border-b border-hoop-border min-w-[140px] align-top">
                      <button
                        onClick={handleAddPlayer}
                        className="px-3 py-2 text-sm bg-hoop-accent text-white rounded hover:opacity-90 whitespace-nowrap"
                      >
                        + Add player
                      </button>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {STATS_ROWS.map((row) => {
                  const bestCols = computeBestColumns(row.key)
                  return (
                    <tr key={row.key} className="border-b border-hoop-border">
                      <td className="sticky left-0 bg-hoop-bg z-10 py-3 px-3 font-semibold">
                        {row.label}
                      </td>
                      {columns.map((col, idx) => {
                        if (!col.data || col.selectedIndex === null) {
                          return (
                            <td key={`${col.slug}-${idx}`} className="text-center py-3 px-3 text-hoop-text-2">
                              —
                            </td>
                          )
                        }
                        const seasons = col.data.seasons.filter((s) => !s.is_3x3)
                        const selected = seasons[col.selectedIndex]
                        if (!selected) {
                          return (
                            <td key={`${col.slug}-${idx}`} className="text-center py-3 px-3 text-hoop-text-2">
                              —
                            </td>
                          )
                        }
                        const val = (selected as any)[row.key] as number | null
                        const isBest = bestCols.has(idx)
                        // Volume tooltips — pct cells carry makes-attempts
                        // sub-text (doubles as the mobile "tooltip") + a
                        // low-volume marker under LOW_VOLUME_ATTEMPTS.
                        const volume =
                          val != null ? volumeFor(selected, row.key) : null
                        const lowVolume =
                          volume != null && volume.attempts < LOW_VOLUME_ATTEMPTS
                        return (
                          <td
                            key={`${col.slug}-${idx}`}
                            className={`text-center py-3 px-3 ${isBest ? 'text-hoop-accent font-semibold' : ''}`}
                            title={
                              volume
                                ? `${volume.makes}-${volume.attempts}${lowVolume ? ' — low volume' : ''}`
                                : undefined
                            }
                          >
                            {row.format(val)}
                            {lowVolume && (
                              <span
                                className="ml-0.5 text-hoop-amber"
                                title={`Low volume: ${volume.makes}-${volume.attempts} attempts`}
                                aria-label="Low attempt volume"
                              >
                                ⚠
                              </span>
                            )}
                            {volume && (
                              <span className="block text-[10px] text-hoop-text-3 font-normal">
                                {volume.makes}-{volume.attempts}
                              </span>
                            )}
                          </td>
                        )
                      })}
                      {columns.length < MAX_COLUMNS && <td className="text-center py-3 px-3"></td>}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footnote */}
        <p className="text-center mt-4 text-xs text-hoop-text-2">
          eFG% and TS% derived from per-game box scores.
        </p>
      </div>

      {/* Player picker modal */}
      {showPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-display font-bold">Choose a player</h2>
              <button
                onClick={() => setShowPicker(false)}
                className="text-hoop-text-2 hover:text-hoop-text"
              >
                ✕
              </button>
            </div>
            <input
              type="text"
              value={pickerQuery}
              onChange={(e) => setPickerQuery(e.target.value)}
              placeholder="Search by name..."
              className="w-full px-4 py-2 border border-hoop-border rounded bg-white dark:bg-hoop-surface text-hoop-text placeholder:text-hoop-text-3 mb-4"
              autoFocus
            />
            <div className="max-h-96 overflow-y-auto space-y-2">
              {pickerLoading && (
                <div className="text-center py-4 text-hoop-text-2">
                  Loading...
                </div>
              )}
              {!pickerLoading && pickerResults.length === 0 && pickerQuery.trim() && (
                <div className="text-center py-4 text-hoop-text-2">
                  No players found
                </div>
              )}
              {!pickerLoading && pickerResults.length === 0 && !pickerQuery.trim() && (
                <div className="text-center py-4 text-hoop-text-2">
                  Start typing to search
                </div>
              )}
              {pickerResults.map((p) => (
                <button
                  key={p.id}
                  onClick={() => handlePickerSelect(p.slug)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-hoop-accent-light rounded text-left"
                >
                  <Avatar
                    src={p.photo_url}
                    name={p.name}
                    className="w-12 h-12 rounded-full flex-shrink-0"
                    textClassName="text-sm"
                  />
                  <div>
                    <p className="font-semibold">{p.name}</p>
                    <p className="text-sm text-hoop-text-2">
                      {p.position || '—'} · {p.nationality || '—'}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
